import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { properties } from "@/lib/properties";
import { getArticleBySlug } from "@/lib/articles";

const SITE = "https://frantisekbabcicky.cz";
const SUFFIX = "František Babčický – realitní makléř Praha";
const DEFAULT_IMAGE = `${SITE}/og-image.jpg`;

type Seo = { title: string; description: string; image?: string };

const clip = (s: string, max = 155) => {
  const t = s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  return cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;–-]\s*$/, "") + "…";
};

const STATIC: Record<string, Seo> = {
  "/": {
    title: "Realitní makléř Praha | František Babčický – RE/MAX Alfa",
    description:
      "Prodejte nemovitost bezpečně s makléřem s 30letou praxí a 1670+ prodanými nemovitostmi. Bezplatný odhad ceny.",
  },
  "/o-mne": { title: `O mně | ${SUFFIX}`, description: "František Babčický – 30 let na realitním trhu." },
  "/reference": {
    title: `Reference klientů | ${SUFFIX}`,
    description: "Video reference klientů. Podívejte se, co říkají spokojení klienti o spolupráci s Františkem Babčickým.",
  },
  "/kontakt": {
    title: `Kontakt | ${SUFFIX}`,
    description: "Napište mi nebo zavolejte. Rád vám pomohu s jakýmkoliv dotazem ohledně nemovitostí.",
  },
  "/sluzby/prodej": {
    title: `Prodej nemovitostí | ${SUFFIX}`,
    description:
      "Prodám váš dům, byt či pozemek za nejvyšší možnou cenu – a postarám se o všechny kroky, které s prodejem nemovitosti souvisí.",
  },
  "/sluzby/odhad": {
    title: `Tržní cena | ${SUFFIX}`,
    description: clip(
      "ZDARMA, bez závazků a do 48 hodin vám prozradím, kolik prodejem svého domu či bytu vyděláte – a doporučím ověřené postupy, jak cenu nemovitosti ještě zvýšit."
    ),
  },
  "/sluzby/odhad/byt": {
    title: `Tržní odhad zdarma – Byt | ${SUFFIX}`,
    description: "Vyplňte formulář a do 48 hodin vám zašleme odhad tržní ceny vašeho bytu.",
  },
  "/sluzby/odhad/dum": {
    title: `Tržní odhad zdarma – Dům | ${SUFFIX}`,
    description: "Vyplňte formulář a do 48 hodin vám zašleme odhad tržní ceny vaší nemovitosti.",
  },
  "/sluzby/odhad/pozemek": {
    title: `Tržní odhad zdarma – Pozemek | ${SUFFIX}`,
    description: "Vyplňte formulář a do 48 hodin vám zašleme odhad tržní ceny vašeho pozemku.",
  },
  "/sluzby/dedictvi": {
    title: `Dědictví – odhad | ${SUFFIX}`,
    description: "Určím tržní hodnotu vaší nemovitosti a připravím dokumenty pro notáře.",
  },
  "/sluzby/aukce": {
    title: `Aukce | ${SUFFIX}`,
    description: "Navýším konečnou cenu za váš dům či byt v průměru o 6 %.",
  },
  "/clanky": {
    title: `Užitečné informace ze světa realit | ${SUFFIX}`,
    description: "Praktické rady, daně, právo a tipy pro majitele i kupující nemovitostí.",
  },
};

const absolute = (url: string) => (url.startsWith("http") ? url : `${SITE}${url.startsWith("/") ? "" : "/"}${url}`);

export const getRouteSeo = (pathname: string): Seo => {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (STATIC[path]) return STATIC[path];

  const prop = path.match(/^\/nemovitost\/([^/]+)$/);
  if (prop) {
    const p = properties.find((x) => x.slug === prop[1]);
    if (p) {
      const showPrice = !(p.sold || p.reserved) || p.showPrice;
      const parts = [
        [p.type, p.disposition].filter(Boolean).join(" "),
        p.location,
        p.size,
        p.landSize ? `${p.landSizeLabel ?? "pozemek"} ${p.landSize}` : "",
        p.sold ? "Prodáno" : p.reserved ? "Rezervováno" : "",
        showPrice ? p.price : "",
      ].filter(Boolean);
      const first = p.images?.[0] ?? p.image;
      return {
        title: `${p.title} | František Babčický – RE/MAX Alfa`,
        description: clip(parts.join(", ")),
        image: first ? absolute(first) : undefined,
      };
    }
    return { title: `Nemovitost nenalezena | ${SUFFIX}`, description: "Nemovitost nenalezena." };
  }

  const art = path.match(/^\/clanky\/([^/]+)$/);
  if (art) {
    const a = getArticleBySlug(art[1]);
    if (a) return { title: `${a.title} | ${SUFFIX}`, description: clip(a.perex) };
  }

  return { title: `Stránka nenalezena | ${SUFFIX}`, description: "Stránka nenalezena." };
};

const setMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const seo = getRouteSeo(pathname);
    const image = seo.image ?? DEFAULT_IMAGE;
    const url = `${SITE}${pathname === "/" ? "/" : pathname.replace(/\/+$/, "")}`;
    document.title = seo.title;
    setMeta("name", "description", seo.description);
    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:image", image);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", seo.title);
    setMeta("name", "twitter:description", seo.description);
    setMeta("name", "twitter:image", image);
  }, [pathname]);
  return null;
};

export default RouteSeo;
